import React, { useContext, useEffect, useRef, useState } from "react"
import { Tooltip } from "antd"
import { useQuery } from "@tanstack/react-query"
import { useHistory } from "react-router-dom"
import moment from "moment"
import i18n from "../../services/i18n"
import { request } from "../../utils/config"
import getDateInterval from "../../utils/getDateInterval"
import { permSync } from "../../utils/permSync"
import { expenseTypeLabel } from "../../utils/expenseTypeLabel"
import { SocketContext } from "../../contexts/SocketContext"
import MoneyTodayDrawer, { turKaliti, maqsadKaliti } from "./MoneyTodayDrawer"   // tog'a #4127
import "./liveCounters.less"

// tog'a #3203 (2026-10-03, C varianti): BUGUNGI sotuv va zakaz soni — «Глобальная панель» tepasida katta kartalar va har sahifa
// sarlavhasida ixcham son. Yangi sotuv/zakaz tushsa son o'zgaradi va «+N» belgisi bir daqiqa yonib turadi.
// Manba: Sales/Today (ro'yxat bilan bir xil doira) va Orders/stats/today. 20 soniyada yangilanadi + zakaz/sotuv socket hodisasida darhol.
// Huquq bo'lmasa so'rov yuborilmaydi va blok chizilmaydi.

const usd = (v: any) => "$" + (Number(v) || 0).toLocaleString("ru-RU", { maximumFractionDigits: 0 })
// Bugungi kun (YYYY-MM-DD) — karta xulosasi va tafsilot oynasi (#4127) BIR XIL kun bilan so'raydi
const bugunKun = () => { const d = new Date(); return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0") }

export function useLiveCounters() {
  const salesOk = permSync("sales").canRead
  const ordersOk = permSync("orders").canRead
  const sales = useQuery(["LIVE_SALES"], async () => (await request({ url: "Sales/Today" })).data, { enabled: salesOk, refetchInterval: 20000, staleTime: 10000 })
  const orders = useQuery(["LIVE_ORDERS"], async () => (await request({ url: "Orders/stats/today" })).data, { enabled: ordersOk, refetchInterval: 20000, staleTime: 10000 })
  // tog'a #3557/#3620 (2026-10-06): «Деньги сегодня» — «Кошелёк» bilan AYNAN bir xil hisob (Wallet/Summary): so'm va dollar alohida,
  // menejer faqat O'Z pulini ko'radi («Мой кошелёк»), «wallet» huquqisiz karta chiqmaydi
  const walletOk = permSync("wallet").canRead
  const money = useQuery(["LIVE_MONEY"], async () => {
    const kun = bugunKun()
    return (await request({ url: "Wallet/Summary", params: { begin: kun, end: kun } })).data
  }, { enabled: walletOk, refetchInterval: 20000, staleTime: 10000 })
  const { lastMessage } = (useContext(SocketContext) || {}) as any
  useEffect(() => {
    const f = String(lastMessage?.nameFunction || "")
    if (/order/i.test(f) && ordersOk) orders.refetch()
    if (/sale/i.test(f) && salesOk) sales.refetch()
    if (/money|wallet|cash/i.test(f) && walletOk) money.refetch()   // #3620: pul hodisasi — karta darhol yangilanadi
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastMessage])
  const saleCount = Number(sales.data?.count || 0), orderCount = Number(orders.data?.total || 0)
  // «+N» — oldingi qiymatdan oshsa, 60 soniya
  const [yangi, setYangi] = useState<{ s: number; o: number }>({ s: 0, o: 0 })
  const oldin = useRef<{ s: number | null; o: number | null }>({ s: null, o: null })
  useEffect(() => {
    if (sales.data == null) return
    const p = oldin.current.s
    if (p != null && saleCount > p) { setYangi((y) => ({ ...y, s: saleCount - p })); setTimeout(() => setYangi((y) => ({ ...y, s: 0 })), 60000) }
    oldin.current.s = saleCount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saleCount, sales.data])
  useEffect(() => {
    if (orders.data == null) return
    const p = oldin.current.o
    if (p != null && orderCount > p) { setYangi((y) => ({ ...y, o: orderCount - p })); setTimeout(() => setYangi((y) => ({ ...y, o: 0 })), 60000) }
    oldin.current.o = orderCount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderCount, orders.data])
  const m = money.data || {}
  return {
    salesOk, ordersOk, walletOk,
    money: {
      ready: money.data != null,
      net: Number(m.income || 0) - Number(m.outcome || 0),
      incomeUSD: Number(m.incomeUSD || 0), incomeUZS: Number(m.incomeUZS || 0),
      outcomeUSD: Number(m.outcomeUSD || 0), outcomeUZS: Number(m.outcomeUZS || 0),
      // tog'a #4127 (2026-10-10): «Расходы» bo'limidagi xarajatlar — katta raqam (outcome) ichida bor edi, «Расход» qatorida yo'q edi
      // (−$16 000, «Расход $0»). Xarajatda qaysi valyutada to'langani saqlanmaydi — $ qiymati qo'shiladi, so'mi oynada.
      expenseUSD: Number(m.outcomeExpense || 0),
      mine: m.scope === "mine",
      summary: money.data || null,
    },
    sale: { count: saleCount, amountUSD: Number(sales.data?.amountUSD || 0), ready: sales.data != null, plus: yangi.s },
    order: { count: orderCount, pending: Number(orders.data?.pending || 0), ready: orders.data != null, plus: yangi.o },
  }
}

// «Продажи» sahifasi sana filtrini begin/end (ofsetli ISO) dan o‘qiydi — bugungi kun shu ko‘rinishda beriladi
// (#4127: «Деньги сегодня» kartasi endi «Деньги» sahifasiga emas — tafsilot oynasiga ochiladi, havolalar oynada)
const som =(v: number) => Math.round(v).toLocaleString("ru-RU") + " " + i18n.t("sumShort", "сум")
const bugunSotuv = () => { const { begin, end } = getDateInterval(moment(), moment()); return "/sales?" + new URLSearchParams({ begin, end }).toString() }

/**
 * Tog'a #342 (katalog #6024, «CashChainChip»): «💵 Наличные на руках» — qo'limdagi kassaga topshirilmagan naqd; menga topshirilayotgan
 * naqd yoki tasdig'imni kutayotgan «Расход из наличных» bo'lsa — qizil belgi. Bosilsa «Кошелёк». Faqat SAV (server enabled=false qaytaradi);
 * hech narsa bo'lmasa chizilmaydi. Socket'dagi pul hodisasida darhol yangilanadi.
 */
function CashHandsChip() {
  const history = useHistory()
  const q = useQuery(["CASH_ME_CHIP"], async () => (await request({ url: "CashHandovers/Me", silent: true } as any)).data, { refetchInterval: 30000, staleTime: 15000, retry: false })
  const { lastMessage } = (useContext(SocketContext) || {}) as any
  useEffect(() => {
    if (/money|wallet|cash/i.test(String(lastMessage?.nameFunction || ""))) q.refetch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastMessage])
  const d = q.data
  if (!d?.enabled) return null
  const mine = d.mine || {}, toMe = d.toMe || {}
  const qolda = Number(mine.uzs) > 0.5 || Number(mine.usd) > 0.005
  const belgi = Number(toMe.count || 0) + Number(d.spendsToApprove || 0) + Number(d.chainsToMe || 0)   // #420: menga uzatilayotgan naqd
  if (!qolda && belgi === 0) return null
  const pul = (uzs: any, usd: any) => [Number(uzs) > 0.5 ? som(Number(uzs)) : null, Number(usd) > 0.005 ? "$" + Number(usd).toLocaleString("ru-RU", { maximumFractionDigits: 2 }) : null].filter(Boolean).join(" + ") || "0"
  const izoh = [
    `${i18n.t("cashOnHands", "Наличные на руках")}: ${pul(mine.uzs, mine.usd)}`,
    Number(toMe.count) > 0 ? `${i18n.t("cashToMe", "Вам сдают")}: ${pul(toMe.uzs, toMe.usd)} (${toMe.count})` : null,
    Number(d.spendsToApprove) > 0 ? `${i18n.t("cashSpendsToApprove", "Расходы на подтверждение")}: ${d.spendsToApprove}` : null,
    Number(d.chainsToMe) > 0 ? `${i18n.t("cashChainsToMe", "Вам передают наличные")}: ${d.chainsToMe}` : null,
  ].filter(Boolean).join(" · ")
  return (
    <Tooltip placement="bottom" title={izoh}>
      <button className="live-chip live-chip-cash" title={izoh} onClick={() => history.push("/wallet")} data-testid="live-chip-cash">
        💵<span className="live-chip-label">{i18n.t("cashOnHandsShort", "На руках")}</span><b>{qolda ? pul(mine.uzs, mine.usd) : "0"}</b>
        {belgi > 0 && <span className="live-plus live-cash-badge" data-testid="live-chip-cash-badge">{belgi}</span>}
      </button>
    </Tooltip>
  )
}

/** Sarlavhadagi ixcham sonlar (har sahifada). Tor sarlavhada faqat belgi + son. */
export function LiveHeaderChips() {
  const L = useLiveCounters()
  const history = useHistory()
  if (!L.salesOk && !L.ordersOk) return <span className="live-chips" data-testid="live-chips"><CashHandsChip /></span>
  return (
    <span className="live-chips" data-testid="live-chips">
      <CashHandsChip />
      {L.salesOk && L.sale.ready && (
        <Tooltip placement="bottom" title={`${i18n.t("liveSalesToday", "Продажи сегодня")}: ${L.sale.count} · ${usd(L.sale.amountUSD)}`}>
          <button className="live-chip live-chip-sale" title={i18n.t("sales", "Продажи")} onClick={() => history.push(bugunSotuv())} data-testid="live-chip-sale">
            🛒<span className="live-chip-label">{i18n.t("sales", "Продажи")}</span><b>{L.sale.count}</b>
            {L.sale.plus > 0 && <span className="live-plus">+{L.sale.plus}</span>}
          </button>
        </Tooltip>
      )}
      {L.ordersOk && L.order.ready && (
        <Tooltip placement="bottom" title={`${i18n.t("liveOrdersToday", "Заказы сегодня")}: ${L.order.count} · ${i18n.t("liveAwaiting", "ждут ответа")}: ${L.order.pending}`}>
          <button className="live-chip live-chip-order" title={i18n.t("orders", "Заказы")} onClick={() => history.push("/orders")} data-testid="live-chip-order">
            📦<span className="live-chip-label">{i18n.t("orders", "Заказы")}</span><b>{L.order.count}</b>
            {L.order.plus > 0 && <span className="live-plus">+{L.order.plus}</span>}
          </button>
        </Tooltip>
      )}
    </span>
  )
}

/** «Глобальная панель» tepasidagi katta kartalar. */
export function LiveBigCards() {
  const L = useLiveCounters()
  const history = useHistory()
  // tog'a #4127 (+++ #4132, A varianti): karta / qator / tur bosilsa — bugungi tafsilot oynasi (turlar → aniq yozuvlar → sahifaga havola)
  const [pul, setPul] = useState<{ ochiq: boolean; focus?: string; day: string }>({ ochiq: false, day: "" })
  const ochish = (focus?: string) => { if (L.money.ready) setPul({ ochiq: true, focus, day: bugunKun() }) }
  const S = L.money.summary || {}
  const nomMaqsad = (g: any) => (g.purposeId == null ? i18n.t("mtNoPurpose", "Без назначения") : g.name)
  // «Расход» turlari: xarajat turi («Расходы») + kassadan to'lov maqsadi («Деньги»), eng kattasidan; «Доход» — to'lov maqsadi
  const chiqimTurlar = [
    ...(S.expenseByType || []).map((g: any) => ({ id: "x-" + turKaliti(g), nom: expenseTypeLabel(g.code, g.code), sum: Number(g.amount) || 0 })),
    ...(S.outcomeByPurpose || []).map((g: any) => ({ id: "o-" + maqsadKaliti(g.purposeId), nom: nomMaqsad(g), sum: Number(g.amount) || 0 })),
  ].filter((x) => x.sum > 0).sort((a, b) => b.sum - a.sum)
  const kirimTurlar = (S.incomeByPurpose || []).map((g: any) => ({ id: "i-" + maqsadKaliti(g.purposeId), nom: nomMaqsad(g), sum: Number(g.amount) || 0 }))
    .filter((x: any) => x.sum > 0).sort((a: any, b: any) => b.sum - a.sum)
  const chiplar = (list: { id: string; nom: string; sum: number }[], tid: string) => (list.length ? (
    <div className="live-card-types" data-testid={tid}>
      {list.slice(0, 3).map((x) => (
        <button type="button" key={x.id} className="live-type-chip" onClick={(e) => { e.stopPropagation(); ochish(x.id) }}>{x.nom} {usd(x.sum)}</button>
      ))}
      {list.length > 3 ? (
        <button type="button" className="live-type-chip" onClick={(e) => { e.stopPropagation(); ochish(list[3].id) }}>{i18n.t("mtMore", "ещё {{n}}", { n: list.length - 3 })}</button>
      ) : null}
    </div>
  ) : null)
  if (!L.salesOk && !L.ordersOk && !L.walletOk) return null
  return (
    <div className="live-cards" data-testid="live-cards">
      {L.salesOk && (
        <div className="live-card live-card-sale" onClick={() => history.push(bugunSotuv())} data-testid="live-card-sale">
          <div className="live-card-t">🛒 {i18n.t("liveSalesToday", "Продажи сегодня")}</div>
          <div className="live-card-n">{L.sale.ready ? L.sale.count : "…"}</div>
          <div className="live-card-sub">{i18n.t("liveOnSum", "на сумму")} <b>{usd(L.sale.amountUSD)}</b></div>
          <div className="live-card-live"><span className="live-dot" />{i18n.t("liveAuto", "обновляется автоматически")}</div>
          {L.sale.plus > 0 && <span className="live-card-new">+{L.sale.plus} {i18n.t("liveNewF", "новая")}</span>}
        </div>
      )}
      {L.ordersOk && (
        <div className="live-card live-card-order" onClick={() => history.push("/orders")} data-testid="live-card-order">
          <div className="live-card-t">📦 {i18n.t("liveOrdersToday", "Заказы сегодня")}</div>
          <div className="live-card-n">{L.order.ready ? L.order.count : "…"}</div>
          <div className="live-card-sub">{i18n.t("liveAwaiting", "ждут ответа")}: <b>{L.order.pending}</b></div>
          <div className="live-card-live"><span className="live-dot" />{i18n.t("liveAuto", "обновляется автоматически")}</div>
          {L.order.plus > 0 && <span className="live-card-new">+{L.order.plus} {i18n.t("liveNewM", "новый")}</span>}
        </div>
      )}
      {L.walletOk && (
        <div className="live-card live-card-money" onClick={() => ochish()} data-testid="live-card-money">
          <div className="live-card-t">💵 {i18n.t("liveMoneyToday", "Деньги сегодня")}{L.money.mine ? <span className="live-card-mine"> · {i18n.t("myWallet", "Мой кошелёк")}</span> : null}</div>
          <div className="live-card-n">{L.money.ready ? (Math.abs(L.money.net) < 0.5 ? "" : L.money.net > 0 ? "+" : "−") + usd(Math.abs(L.money.net)) : "…"}</div>
          <div className="live-card-rows">
            <div className="live-card-row" onClick={(e) => { e.stopPropagation(); ochish("in") }} data-testid="live-money-in">
              <span>{i18n.t("income", "Доход")}</span><b>{usd(L.money.incomeUSD)}{L.money.incomeUZS > 0 ? " · " + som(L.money.incomeUZS) : ""}<span className="live-chev">›</span></b>
            </div>
            {chiplar(kirimTurlar, "live-money-in-types")}
            <div className="live-card-row" onClick={(e) => { e.stopPropagation(); ochish("out") }} data-testid="live-money-out">
              <span>{i18n.t("expense", "Расход")}</span><b>{usd(L.money.outcomeUSD + L.money.expenseUSD)}{L.money.outcomeUZS > 0 ? " · " + som(L.money.outcomeUZS) : ""}<span className="live-chev">›</span></b>
            </div>
            {chiplar(chiqimTurlar, "live-money-out-types")}
          </div>
          <div className="live-card-live"><span className="live-dot" />{i18n.t("liveAuto", "обновляется автоматически")}</div>
        </div>
      )}
      {/* Oyna karta DIV'idan tashqarida: portal ichidagi bosish React bo'yicha ota-elementga ko'tariladi — karta ichida bo'lsa ✕ bosilganda qayta ochilardi */}
      {L.walletOk && (
        <MoneyTodayDrawer open={pul.ochiq} focus={pul.focus} day={pul.day || bugunKun()} summary={L.money.summary} onClose={() => setPul((p) => ({ ...p, ochiq: false }))} />
      )}
    </div>
  )
}
